// The files page: every file in the plan the reader can see, and the way to add one.

import { useCallback, useEffect, useState } from 'react';

import type { ApiClient } from '../api/client.js';
import type { FileView } from '../api/contract.js';
import { viewerIsPrincipal } from '../plan/snapshot.js';
import type { PlanState } from '../plan/usePlan.js';
import { AddFile } from './AddFile.js';
import { FileCard } from './FileRows.js';

export type FilesPageProps = {
  state: PlanState;
  client: ApiClient;
  planId: string;
};

export function FilesPage({ state, client, planId }: FilesPageProps): JSX.Element | null {
  const snapshot = state.snapshot;
  const [files, setFiles] = useState<FileView[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    let current = true;
    client.listFiles(planId).then(
      (loaded) => {
        if (current) {
          setFiles(loaded);
          setProblem(null);
        }
      },
      () => {
        if (current) {
          setProblem('The files didn’t load. Try again.');
        }
      },
    );
    return () => {
      current = false;
    };
  }, [client, planId, snapshot, version]);

  if (snapshot === null) {
    return null;
  }
  const principal = viewerIsPrincipal(snapshot);

  return (
    <section className="filesPage">
      <h1 className="page__title">Files</h1>
      <p className="page__subtitle">Files and documents uploaded to this plan.</p>
      <AddFile
        client={client}
        planId={planId}
        people={snapshot.people}
        viewerIsPrincipal={principal}
        originThreadId={null}
        onAdded={reload}
      />
      {problem === null ? null : (
        <p className="page__problem" role="alert">
          {problem}
        </p>
      )}
      {files === null ? null : files.length === 0 ? null : (
        <ul className="fileList">
          {files.map((file) => (
            <FileCard
              key={file.id}
              client={client}
              planId={planId}
              file={file}
              people={snapshot.people}
              viewerIsPrincipal={principal}
              onChanged={reload}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
